// Persistence + canonical constants for the event_reminders table.
//
// Pattern: load → edit in memory → replaceAll on save. Diff is cheap, the
// row count per event is capped at MAX_REMINDERS, and "replace" plays well
// with the RLS policies (each row is auth.uid()-scoped and the user is the
// only writer).

import { supabase } from "@/integrations/supabase/client";

export const MAX_REMINDERS = 2;

// Canonical offset preset (minutes BEFORE start). Kept short on purpose —
// the modal renders these as chips + the "+ add" button is disabled past
// the cap. i18n keys live in src/i18n/locales/*.ts under reminders.preset.*.
export interface ReminderOption {
  value: number; // minutes before start
  labelKey: string;
}

export const REMINDER_OPTIONS: ReminderOption[] = [
  { value: 0, labelKey: "reminders.preset.atStart" },
  { value: 10, labelKey: "reminders.preset.10min" },
  { value: 30, labelKey: "reminders.preset.30min" },
  { value: 60, labelKey: "reminders.preset.1hour" },
  { value: 1440, labelKey: "reminders.preset.1day" },
];

export function findReminderLabelKey(minutes: number): string {
  const match = REMINDER_OPTIONS.find((o) => o.value === minutes);
  if (match) return match.labelKey;
  return "reminders.preset.custom";
}

export interface EventReminderRow {
  id: string;
  event_id: string;
  user_id: string;
  reminder_minutes: number;
  created_at: string;
}

export async function loadEventReminders(eventId: string): Promise<number[]> {
  const { data, error } = await (supabase as any)
    .from("event_reminders")
    .select("reminder_minutes")
    .eq("event_id", eventId)
    .order("reminder_minutes", { ascending: true });
  if (error) {
    console.error("[agendaReminders] load failed:", error, { eventId });
    return [];
  }
  return (data as { reminder_minutes: number }[] | null)?.map((r) => r.reminder_minutes) ?? [];
}

// Batch-load reminders for many event ids. Used by the agenda list to
// render the 🔔 badge without N+1ing the DB. Returns a Map keyed by
// event_id, value is the count.
export async function loadReminderCounts(eventIds: string[]): Promise<Record<string, number>> {
  if (eventIds.length === 0) return {};
  const { data, error } = await (supabase as any)
    .from("event_reminders")
    .select("event_id")
    .in("event_id", eventIds);
  if (error) {
    console.error("[agendaReminders] count load failed:", error);
    return {};
  }
  const counts: Record<string, number> = {};
  for (const row of (data ?? []) as { event_id: string }[]) {
    counts[row.event_id] = (counts[row.event_id] ?? 0) + 1;
  }
  return counts;
}

// Replace all reminders for an event with the given set. Two-step (DELETE
// then INSERT) under RLS — the user owns both rows so each call is a
// single auth-scoped statement. The MAX_REMINDERS cap is enforced in the
// UI; the helper trims defensively here too in case a stale state slipped
// through.
export async function replaceEventReminders(params: {
  eventId: string;
  userId: string;
  reminders: number[];
}): Promise<{ ok: boolean; error?: string }> {
  const { eventId, userId, reminders } = params;
  const trimmed = Array.from(new Set(reminders))
    .filter((n) => Number.isFinite(n) && n >= 0 && n <= 10080)
    .slice(0, MAX_REMINDERS);

  try {
    const { error: delErr } = await (supabase as any)
      .from("event_reminders")
      .delete()
      .eq("event_id", eventId);
    if (delErr) {
      console.error("[agendaReminders] delete failed:", delErr, { eventId });
      return { ok: false, error: delErr.message };
    }
    if (trimmed.length === 0) return { ok: true };
    const rows = trimmed.map((minutes) => ({
      event_id: eventId,
      user_id: userId,
      reminder_minutes: minutes,
    }));
    const { error: insErr } = await (supabase as any)
      .from("event_reminders")
      .insert(rows);
    if (insErr) {
      console.error("[agendaReminders] insert failed:", insErr, { eventId, count: rows.length });
      return { ok: false, error: insErr.message };
    }
    return { ok: true };
  } catch (e) {
    console.error("[agendaReminders] replace unexpected:", e);
    return { ok: false, error: e instanceof Error ? e.message : "unknown" };
  }
}
